import { Form } from "components/Form/components/Form/Form";
import { SwitchFormComponent } from "components/Form/components/Switch/SwitchFormComponent";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { Popover, popoverPosition } from "components/Popover/Popover";
import { useMemo } from "react";
import { Field } from "react-final-form";

interface ShowDeletedProps {
    includeDeleted: boolean;
    hideDeleted: boolean;
    onBlur?: () => void;
}

export const ShowDeletedFilter = ({ includeDeleted, hideDeleted, onBlur }: ShowDeletedProps) => {
    const label = useMemo(() => {
        if (includeDeleted && hideDeleted) return "Include in trash";
        if (includeDeleted && !hideDeleted) return "Only in trash";
        if (hideDeleted) return "Hide in trash";

        return "Include nothing";
    }, [includeDeleted, hideDeleted]);

    return (
        <Popover
            trigger={
                <button
                    type="button"
                    className={`button button--white showFilterButton${
                        label === "Include nothing" ? " button--warning" : ""
                    }`}
                >
                    {label}
                    <Icon icon={IconCollection.chevronDown} />
                </button>
            }
            closeOnInsideClick={false}
            closeOnEnter={true}
            onBlur={onBlur}
            {...popoverPosition}
        >
            <div className="showFilterPopoverContent">
                <div className="option">
                    <Form.Row>
                        <Field
                            name="include_deleted"
                            id="include_deleted"
                            component={SwitchFormComponent}
                            fieldProps={{
                                label: "Show in trash",
                            }}
                            fieldAttr={{
                                size: "md",
                            }}
                        />
                    </Form.Row>
                </div>
                <div className="option">
                    <Form.Row>
                        <Field
                            name="hide_deleted"
                            id="hide_deleted"
                            component={SwitchFormComponent}
                            fieldProps={{
                                label: "Show non-trashed",
                            }}
                            fieldAttr={{
                                size: "md",
                            }}
                        />
                    </Form.Row>
                </div>
            </div>
        </Popover>
    );
};

interface ShowBundleContentFilterProps {
    showBundleContent: boolean;
    showNonBundleContent: boolean;
    onBlur?: () => void;
}

export const ShowBundleContentFilter = ({
    showBundleContent,
    showNonBundleContent,
    onBlur,
}: ShowBundleContentFilterProps) => {
    const label = useMemo(() => {
        if (showBundleContent && showNonBundleContent) return "Include bundle content";
        if (showBundleContent) return "Only bundle content";
        if (showNonBundleContent) return "Only non-bundle content";

        return "Include bundle content";
    }, [showNonBundleContent, showBundleContent]);

    return (
        <Popover
            trigger={
                <button type="button" className={`button button--white showFilterButton`}>
                    {label}
                    <Icon icon={IconCollection.chevronDown} />
                </button>
            }
            closeOnInsideClick={false}
            onBlur={onBlur}
            {...popoverPosition}
        >
            <div className="showFilterPopoverContent">
                <div className="option">
                    <Form.Row>
                        <Field
                            name="showBundleContent"
                            id="showBundleContent"
                            component={SwitchFormComponent}
                            fieldProps={{
                                label: "Show bundle content",
                            }}
                            fieldAttr={{
                                size: "md",
                            }}
                        />
                    </Form.Row>
                </div>
                <div className="option">
                    <Form.Row>
                        <Field
                            name="showNonBundleContent"
                            id="showNonBundleContent"
                            component={SwitchFormComponent}
                            fieldProps={{
                                label: "Show non-bundle content",
                            }}
                            fieldAttr={{
                                size: "md",
                            }}
                        />
                    </Form.Row>
                </div>
            </div>
        </Popover>
    );
};

interface TrashBinFilterProps {
    showInTrash: boolean;
    showPendingDelete: boolean;
    showPendingMoveToTrash: boolean;
    showPendingRestore: boolean;
    onBlur?: () => void;
}

const TRASH_BIN_STATE_COUNT = 4;

export const TrashBinFilter = ({
    showInTrash,
    showPendingDelete,
    showPendingMoveToTrash,
    showPendingRestore,
    onBlur,
}: TrashBinFilterProps) => {
    const label = useMemo(() => {
        const states: string[] = [];

        if (showInTrash) {
            states.push("In trash");
        }
        if (showPendingMoveToTrash) {
            states.push("Pending move to trash");
        }
        if (showPendingDelete) {
            states.push("Pending delete");
        }
        if (showPendingRestore) {
            states.push("Pending restore");
        }

        if (!states.length) {
            return "Show nothing";
        }

        if (states.length === TRASH_BIN_STATE_COUNT) {
            return "All trash states";
        }

        return states.join(" + ");
    }, [showInTrash, showPendingDelete, showPendingMoveToTrash, showPendingRestore]);

    return (
        <span className="trashBinFilter">
            <span className="trashBinFilter__label">Show: </span>
            <Popover
                trigger={
                    <button
                        type="button"
                        className={`button button--white showFilterButton${
                            label === "Show nothing" ? " button--warning" : ""
                        }`}
                    >
                        {label}
                        <Icon icon={IconCollection.chevronDown} />
                    </button>
                }
                closeOnInsideClick={false}
                closeOnEnter={true}
                closeOnOutsideClick={true}
                onBlur={onBlur}
                toggleClassName="trashBinFilter__toggle"
                {...popoverPosition}
            >
                <div className="showFilterPopoverContent">
                <div className="option">
                    <Form.Row>
                        <Field
                            name="showInTrash"
                            id="showInTrash"
                            component={SwitchFormComponent}
                            fieldProps={{
                                label: "In trash",
                            }}
                            fieldAttr={{
                                size: "md",
                            }}
                        />
                    </Form.Row>
                </div>
                <div className="option">
                    <Form.Row>
                        <Field
                            name="showPendingDelete"
                            id="showPendingDelete"
                            component={SwitchFormComponent}
                            fieldProps={{
                                label: "Pending delete",
                            }}
                            fieldAttr={{
                                size: "md",
                            }}
                        />
                    </Form.Row>
                </div>
                <div className="option">
                    <Form.Row>
                        <Field
                            name="showPendingMoveToTrash"
                            id="showPendingMoveToTrash"
                            component={SwitchFormComponent}
                            fieldProps={{
                                label: "Pending move to trash",
                            }}
                            fieldAttr={{
                                size: "md",
                            }}
                        />
                    </Form.Row>
                </div>
                <div className="option">
                    <Form.Row>
                        <Field
                            name="showPendingRestore"
                            id="showPendingRestore"
                            component={SwitchFormComponent}
                            fieldProps={{
                                label: "Pending restore",
                            }}
                            fieldAttr={{
                                size: "md",
                            }}
                        />
                    </Form.Row>
                </div>
            </div>
            </Popover>
        </span>
    );
};

